import { SiteType } from '@klareospace/pharos-lib-types';
import { cn } from '@klareospace/pharos-lib-web-utils';
import { ChevronDown, ChevronRight, Layers, MapPin } from 'lucide-react';
import * as React from 'react';
import { SITE_TYPE_ICON_MAP } from '@klareospace/pharos-lib-web-utils';
import { BreadcrumbWrapper } from './breadcrumb-wrapper';
import { KlareoIcon } from './klareo-icon';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface SiteStateProps {
  interactive?: boolean;
  siteColor?: string;
  siteName: string;
  /** When undefined (e.g. for drivers/carriers who lack sites:list), a generic MapPin icon is rendered. */
  siteType?: SiteType;
  spaceName?: string;
  tenantName: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveSiteIcon(siteType: SiteType | undefined): React.ElementType {
  if (!siteType) return MapPin;
  return SITE_TYPE_ICON_MAP[siteType];
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SiteState({
  interactive = true,
  siteColor,
  siteName,
  siteType,
  spaceName,
  tenantName,
}: SiteStateProps): React.JSX.Element {
  const SiteIcon = resolveSiteIcon(siteType);

  return (
    <BreadcrumbWrapper interactive={interactive}>
      <div className="flex items-center gap-0.5">
        <div
          className={cn(
            'flex items-center gap-0.5 overflow-hidden transition-all duration-500 ease-in-out',
            'max-w-0 opacity-0 group-hover/breadcrumb:max-w-[500px] group-hover/breadcrumb:opacity-100',
            interactive && 'group-data-[state=open]/trigger:max-w-[500px] group-data-[state=open]/trigger:opacity-100',
          )}
        >
          <KlareoIcon />
          <span className="text-xs md:text-sm text-muted-foreground whitespace-nowrap truncate max-w-[200px]">
            {tenantName}
          </span>
          <ChevronRight className="size-3.5 mx-0.5 text-muted-foreground/50 shrink-0" />
          {spaceName && (
            <>
              <Layers className="size-3.5 text-muted-foreground" />
              <span className="text-xs md:text-sm text-muted-foreground whitespace-nowrap truncate max-w-[200px]">
                {spaceName}
              </span>
              <ChevronRight className="size-3.5 mx-0.5 text-muted-foreground/50 shrink-0" />
            </>
          )}
        </div>
        <div className="flex items-center gap-1 text-xs md:text-sm font-medium text-foreground">
          <SiteIcon className="size-3.5" style={{ color: siteColor }} />
          <span className="truncate max-w-[200px]">{siteName}</span>
        </div>
        {interactive && (
          <ChevronDown className="size-3 ms-1 text-muted-foreground/40 group-hover/breadcrumb:text-muted-foreground group-data-[state=open]/trigger:text-muted-foreground transition-colors" />
        )}
      </div>
    </BreadcrumbWrapper>
  );
}
